import React from 'react';
import Header from 'layout/Header';
import Footer from 'layout/Footer';
import WizardStepsForOverallProgress from 'components/WizardStepsForOverallProgress';
import { Row, Col, Container } from 'react-bootstrap';
import { TechemTheme } from '@techem/techem-theme';
import { Paths } from 'Routes';
import { DetailedEventOverview } from 'components/DetailedEventOverview';
import Title from 'components/Title';
import { Trans, useTranslation } from 'react-i18next';
import { OrderType, StoreObject } from 'reducers/onboardingInformationSlice';
import { connect } from 'react-redux';
import Icon, { ICONSIZE } from 'components/Icon';

import checkmarkIcon from 'assets/icons/png/3x/icn-check.png';
import SurveyView from 'components/SurveyView';
import { KIND, SIZE } from 'baseui/button';
import { Button } from '@techem/ui-components';
import { TrackerUtil } from 'tracking/TrackerUtil';
import { CSSAssignBorderRadius } from 'services/tools/CSSHelper';
import { SURVICATE_SURVEY_ID_INSTALLATION } from 'config';
import { useLocation, useNavigate } from 'react-router-dom';

const mapStateToProps = (state: any) => {
  return {
    onboardingInfo: state.onboardingInfo
  };
};

interface Props {
  onboardingInfo?: StoreObject;
}

const InstallationCompletedPage: React.FC<Props> = ({ onboardingInfo }) => {
  const navigate = useNavigate();
  const loc = useLocation();
  const { t } = useTranslation();

  return (
    <div className="d-flex flex-column min-vh-100">
      <Header>
        <WizardStepsForOverallProgress
          clickOnShowAll={() => {
            navigate(
              {
                pathname: Paths.ProgressOverview,
                search: loc.search
              },
              {
                state: { from: loc.pathname }
              }
            );
          }}
        />
      </Header>

      <main className="flex-grow-1 flex-shrink-1">
        <Container>
          <Row className="justify-content-center">
            <Col xs={12} md={10} lg={8} className="mb-3">
              <Title className="mx-1" title={t('installationCompletedPageTitle')} />
            </Col>
          </Row>

          <Row className="justify-content-center">
            <Col
              xs={12}
              md={10}
              lg={8}
              style={{
                backgroundColor: TechemTheme.colors.backgroundSecondary,
                ...CSSAssignBorderRadius('8px')
              }}
            >
              <Row className="justify-content-center">
                <Col xs="auto" className="copy p-0 mt-3 pt-2">
                  <Icon iconSrc={checkmarkIcon} alt="checkmark" size={ICONSIZE.xxxlarge} />
                </Col>
              </Row>

              <Row className="justify-content-center">
                <Col xs={10} md={8} lg={6} className="copy p-0 mt-3 mb-2 pb-md-3 text-center">
                  <Trans i18nKey="installationCompletedPageContent" />
                </Col>
              </Row>
            </Col>
          </Row>

          <Row className="justify-content-center">
            <Col xs={12} md={10} lg={8} className="copy text-bold pt-2 pt-md-3 m-2 mt-4 mx-0">
              {t('installationCompletedPageSurveySectionTitle')}
            </Col>
          </Row>

          <Row className="justify-content-center">
            <Col xs={12} md={10} lg={8} className="mt-3">
              <Row>
                <Col>
                  <SurveyView
                    surveyId={SURVICATE_SURVEY_ID_INSTALLATION}
                    tdObjectId={
                      onboardingInfo!.userData.properties[
                        onboardingInfo!.selectedPropertyIndex
                      ].orders.find((order) => order.type === OrderType.TechemDirect)!.orderNumber
                    }
                  />
                </Col>
              </Row>
            </Col>
          </Row>

          <Row className="justify-content-center">
            <Col xs="auto" className="mt-4 mb-2 mb-md-3 pt-3 pt-md-2">
              <Button
                size={SIZE.large}
                kind={KIND.secondary}
                onClick={() => {
                  navigate({
                    pathname: Paths.OnboardingCompleted,
                    search: loc.search
                  });
                }}
                additionalTestId="continue-to-onboarding-completed"
                trackClickUsingTestId={(buttonIdentifier) => {
                  TrackerUtil.trackBtnClick(
                    `${TrackerUtil.getPageName()}_button_${buttonIdentifier}`,
                    `button_${buttonIdentifier}`
                  );
                }}
              >
                <Trans i18nKey="installationCompletedPageContinueButtonText" />
              </Button>
            </Col>
          </Row>

          <Row className="justify-content-center">
            <Col xs={12} md={10} lg={8} className="mt-md-3 pt-5">
              <DetailedEventOverview
                data={
                  onboardingInfo!.userData.properties.length > 0
                    ? onboardingInfo!.userData.properties[
                        onboardingInfo!.selectedPropertyIndex
                      ].orders
                        .find((order) => order.type === OrderType.TechemDirect)!
                        .eventHistory.filter((eventItem) => !!eventItem.data)
                    : []
                }
              />
            </Col>
          </Row>
        </Container>
      </main>

      <Footer />
    </div>
  );
};

export default connect(mapStateToProps)(InstallationCompletedPage);
